import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRACTICE_HIGHLIGHTS } from "../lib/brand";

/**
 * Honest trust section. No invented client quotes — only a transparent
 * statement that BuildOnce Studio is new, anchored by Jophy's prior
 * independent practice highlights (anonymised by discipline / sector / region).
 */
export default function TrustSection({ variant = "home" }) {
  const isHome = variant === "home";
  return (
    <section
      data-testid={`trust-section-${variant}`}
      className={`border-b border-bos-ice ${isHome ? "bg-bos-white" : "bg-bos-ice/40"}`}
    >
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-16 items-start">
          <div>
            <p className="bos-eyebrow">Honest first</p>
            <h2 className="mt-4 text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
              A new studio. Practice behind it.
            </h2>
            <span className="bos-divider mt-8" />
          </div>
          <div>
            <p className="text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
              BuildOnce Studio is new. The studio is the next chapter of work,
              not the start of it. It rests on 20+ years of experience in
              business development, brand strategy, and culture advisory,
              working independently with founder-led businesses, expert-led
              consultancies, and leadership teams across Singapore and
              Southeast Asia.
            </p>
            <p className="mt-5 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
              We are working with our first BuildOnce clients now. If
              that suits the season you are in, we would love to talk.
            </p>

            <div className="mt-12">
              <p className="bos-eyebrow">Selected engagements</p>
              <ul
                className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="practice-highlights"
              >
                {PRACTICE_HIGHLIGHTS.map((p, i) => (
                  <li
                    key={`${p.discipline}-${i}`}
                    data-testid={`practice-highlight-${i}`}
                    className="flex flex-col bg-bos-white border border-bos-ice p-5 rounded-sm hover:border-bos-mid/60 transition-colors"
                  >
                    <div className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-3 text-[15px] font-medium text-bos-navy">
                      {p.discipline}
                    </div>
                    <div className="mt-1 text-[13px] text-bos-charcoal/80 leading-[1.6]">
                      {p.sector}
                    </div>
                    <div className="mt-3 text-[12px] text-bos-charcoal/60 tracking-[0.04em]">
                      {p.region}
                    </div>
                    {p.result && (
                      <div className="mt-5 pt-4 border-t border-bos-ice">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bos-primary">
                          Result
                        </div>
                        <p className="mt-2 text-[13px] text-bos-charcoal/85 leading-[1.65]">
                          {p.result}
                        </p>
                        {p.caseStudyUrl && (
                          <a
                            href={p.caseStudyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`practice-highlight-${i}-case-study`}
                            className="mt-4 inline-flex items-center text-[12px] font-medium text-bos-navy underline underline-offset-4 hover:text-bos-primary transition-colors"
                          >
                            View Case Study →
                          </a>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[12px] text-bos-charcoal/60">
                Real engagements. Client names and identifying details withheld.
              </p>
            </div>

            {isHome && (
              <div className="mt-10">
                <Link
                  to="/case-studies/illustrative-clarity-sprint"
                  data-testid="trust-section-case-study-link"
                  className="inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[14px]"
                >
                  See how a Clarity Sprint unfolds
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
